import SectionHeader from "../common/SectionHeader";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import useIsMobile from "../../hooks/useIsMobile";
import { capitalizeFirstLetter } from "../../utils/capitalizeFirstLetter";
import { getShortClanNameFromLong } from "../../utils/getShortClanNameFromLong";
import Table2 from "../common/Table2";
import { NavLink } from "react-router-dom";
import { convertNYTimeToUTC } from "../../utils/convertNYTimeToUTC";

const styles = {
  wrapper: {},
  tooltipValue: {
    color: "rgb(174, 217, 224)"
  },
  title: {
    color: "rgb(70, 130, 180)",
    marginBottom: "16px",
    marginTop: "32px"
  }
};

// Function to build query string
const buildQuery = (player, entries) => {
  const queryParams = [];

  if (player) {
    queryParams.push(`player=${encodeURIComponent(player)}`);
  }

  if (entries) {
    queryParams.push(`entries=${encodeURIComponent(entries)}`);
  }

  // Combine all query parameters
  return queryParams.length ? `?${queryParams.join("&")}` : "";
};

/**
 * Gets tier and returns base tier without redoes
 * @param {string} tier
 * @returns
 */
const parseTier = (tier) => {
  if (tier) {
    const isRedoer = tier.search("\\+");
    if (isRedoer !== -1) {
      return tier.slice(0, isRedoer);
    }
    return tier;
  }
  return "";
};

const getDateFormat = (date, isMobile) => {
  if (!isMobile) {
    // if (moment(date, "YYYY-MM-DDTHH:mm:ss").isSame(new Date(), "day")) {
    //   return "Today " + moment(date, "YYYY-MM-DDTHH:mm:ss").format("h:mm A");
    // }
    // return moment(date, "YYYY-MM-DDTHH:mm:ss").format("ddd h:mm A");
    return capitalizeFirstLetter(convertNYTimeToUTC(date).fromNow());
  }
  return moment(date, "YYYY-MM-DDTHH:mm:ss").format("ddd h:mm A");
};

const desktopColumns = ["Date", "PKer", "Type", "PKee", "Assists"];
const desktopColumnSizes = ["col-2", "col-2", "col-2", "col-2", "col"];
const mobileColumns = ["PKer", "Type", "PKee"];

const dataColors = ["#c7eeff", "#c7eeff", "#bddaa5", "#e04b5a", "#c7eeff"];

const getTooltipData = (item, type) => {
  if (item && type) {
    const _data = type === "pkee" ? item.pkee : item.pker;
    const _clan = type === "pkee" ? item.killedClan : item.killerClan;
    return (
      <div className="d-flex flex-column align-items-start">
        <div>
          Tier: <span style={styles.tooltipValue}>{_data.tier}</span>
        </div>
        <div>
          Level: <span style={styles.tooltipValue}>{_data.level}</span>
        </div>
        <div>
          Race: <span style={styles.tooltipValue}>{_data.race}</span>
        </div>
        <div>
          Class: <span style={styles.tooltipValue}>{_data.class}</span>
        </div>
        <div>
          Clan:{" "}
          <span style={styles.tooltipValue}>
            {capitalizeFirstLetter(getShortClanNameFromLong(_clan))}
          </span>
        </div>
      </div>
    );
  }
};

const getDateColor = (date) => {
  const dateToCheck = moment(date, "YYYY-MM-DDTHH:mm:ss");

  // Check if the date is today
  const isToday = dateToCheck.isSame(moment(), "day");

  if (isToday) {
    return "rgb(255, 165, 0)";
  } else {
    return dataColors[0];
  }
};

const getData = (data = [], isMobile) => {
  let res = [];
  data
    .filter((e) => e.pkee && e.pker)
    .forEach((item) => {
      const _itemToPush = [
        !isMobile && (
          <div
            style={{ color: getDateColor(item.date) }}
            className="d-flex flex-column"
          >
            <span>{getDateFormat(item.date, isMobile)}</span>
            <span className="opacity-50">
              {moment(item.date).format("DD MMM HH:mm:ss")}
            </span>
          </div>
        ),
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{getTooltipData(item, "pker")}</Tooltip>}
        >
          <span style={{ color: dataColors[2] }}>
            <div className="d-flex flex-row align-items-center">
              {item.killer}
              <NavLink
                to={`/player?player=${item.killer}`}
                className="d-flex ms-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-box-arrow-up-right"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5"
                  />
                  <path
                    fillRule="evenodd"
                    d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z"
                  />
                </svg>
              </NavLink>
            </div>
            <div className="d-flex flex-row align-items-center">
              <span className="text-light">
                {getShortClanNameFromLong(item.killerClan)}
                <span className="opacity-50">{` - T${parseTier(item.pker?.tier)} ${item.pker?.level}`}</span>
              </span>
            </div>
          </span>
        </OverlayTrigger>,
        // Type
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{item.type?.toUpperCase()}</Tooltip>}
        >
          <span style={{ color: dataColors[2] }}>
            <div
              className="d-flex flex-row align-items-center"
              style={{ position: "relative", top: "14px" }}
            >
              {item.type === "hc" ? (
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  width="20"
                >
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g strokeLinecap="round" strokeLinejoin="round"></g>
                  <g id="SVGRepo_iconCarrier">
                    {" "}
                    <path
                      d="M16 0H13L3.70711 9.29289L2.20711 7.79289L0.792893 9.20711L3.08579 11.5L1.5835 13.0023C1.55586 13.0008 1.52802 13 1.5 13C0.671573 13 0 13.6716 0 14.5C0 15.3284 0.671573 16 1.5 16C2.32843 16 3 15.3284 3 14.5C3 14.472 2.99923 14.4441 2.99771 14.4165L4.5 12.9142L6.79289 15.2071L8.20711 13.7929L6.70711 12.2929L16 3V0Z"
                      fill="#FF4C4C"
                    ></path>{" "}
                  </g>
                </svg>
              ) : (
                <svg
                  fill="#4C9BFF"
                  height="20"
                  width="20"
                  version="1.1"
                  id="Layer_1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 510.31 510.31"
                >
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g
                    id="SVGRepo_tracerCarrier"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></g>
                  <g id="SVGRepo_iconCarrier">
                    {" "}
                    <g>
                      {" "}
                      <g>
                        {" "}
                        <path d="M504.06,443.728c-8.341-8.341-21.845-8.341-30.165,0h-0.021L412.946,382.8c20.096-23.915,34.731-50.389,36.928-72.768 c1.131-11.733-7.424-22.165-19.157-23.317c-11.925-1.195-22.165,7.445-23.317,19.157c-0.256,2.773-1.067,5.803-2.091,8.917 l-59.648-59.627l128.235-128.235c2.325-2.347,4.096-5.205,5.141-8.341l30.165-90.496c2.56-7.68,0.555-16.128-5.141-21.845 c-5.717-5.717-14.187-7.701-21.845-5.141L391.719,31.27c-3.136,1.045-5.995,2.816-8.341,5.163L255.143,164.646L126.93,36.432 c-2.325-2.347-5.184-4.117-8.341-5.163L28.092,1.104c-7.616-2.56-16.128-0.597-21.824,5.141 c-5.717,5.717-7.723,14.165-5.163,21.845l30.165,90.496c1.045,3.136,2.816,5.995,5.163,8.341l128.213,128.213l-59.904,59.925 c-0.917-2.965-1.621-5.824-1.771-8.405c-0.704-11.755-10.88-20.693-22.592-20.011c-11.776,0.725-20.693,10.837-19.989,22.592 c1.344,22.251,16.149,49.237,36.864,73.643l-60.821,60.843c-8.341-8.341-21.845-8.341-30.165,0c-8.341,8.32-8.341,21.824,0,30.165 l30.165,30.165c4.16,4.16,9.621,6.251,15.083,6.251s10.901-2.091,15.083-6.251c8.32-8.341,8.32-21.845,0-30.165l60.907-60.928 c23.915,20.096,50.411,34.709,72.789,36.885c0.725,0.085,1.408,0.107,2.091,0.107c10.859,0,20.139-8.235,21.205-19.264 c1.152-11.712-7.445-22.165-19.157-23.296c-2.773-0.277-5.803-1.067-8.917-2.112l59.648-59.627l59.904,59.904 c-2.965,0.917-5.824,1.621-8.405,1.771c-11.776,0.704-20.715,10.816-20.011,22.592c0.683,11.307,10.091,20.032,21.269,20.032 c0.448,0,0.875,0,1.323-0.043c22.251-1.344,49.216-16.149,73.621-36.864l60.843,60.843c-8.32,8.32-8.32,21.824,0,30.165 c4.181,4.16,9.643,6.251,15.104,6.251c5.44,0,10.901-2.091,15.083-6.251l30.165-30.165 C512.38,465.552,512.38,452.048,504.06,443.728z M157.927,382.544c-5.653-4.587-11.2-9.557-16.448-14.784 c-0.149-0.149-0.299-0.32-0.448-0.469c-4.715-4.736-9.237-9.728-13.419-14.784l67.2-67.179l30.187,30.165L157.927,382.544z M298.194,207.696l-12.864-12.885L409.98,70.16l45.269-15.083l-15.104,45.248L315.495,224.998L298.194,207.696z"></path>{" "}
                      </g>{" "}
                    </g>{" "}
                  </g>
                </svg>
              )}
            </div>
          </span>
        </OverlayTrigger>,
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{getTooltipData(item, "pkee")}</Tooltip>}
        >
          <span style={{ color: dataColors[3] }}>
            <div className="d-flex flex-row align-items-center h-100">
              <span>{item.killed}</span>
              <span>
                <NavLink
                  to={`/player?player=${item.killed}`}
                  className="d-flex ms-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-box-arrow-up-right"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5"
                    />
                    <path
                      fillRule="evenodd"
                      d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z"
                    />
                  </svg>
                </NavLink>
              </span>
            </div>

            <div className="d-flex flex-row align-items-center">
              <span className="text-light">
                {getShortClanNameFromLong(item.killedClan)}
                <span className="opacity-50">{` - T${parseTier(item.pkee?.tier)} ${item.pkee?.level}`}</span>
              </span>
            </div>
          </span>
        </OverlayTrigger>,
        !isMobile ? (
          item.assists.length > 0 ? (
            <span style={{ color: dataColors[4] }}>
              {item.assists.map((assist) => assist.assistKiller).join(", ")}
            </span>
          ) : (
            []
          )
        ) : (
          ""
        )
      ].filter((e) => e);
      res.push(_itemToPush);
    });
  return res;
};

function Last10Feed() {
  // refs
  const playerInputRef = useRef(null);

  // Table and loading
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);

  // is mobile
  const isMobile = useIsMobile();

  // form values
  const [playerValue, setPlayerValue] = useState();
  const [entriesValue, setEntriesValue] = useState();
  const [playerValueUpdated, setPlayerValueUpdated] = useState();
  const [entriesValueUpdated, setEntriesValueUpdated] = useState();

  // form handlers
  const handlePlayerChange = (e) => {
    setPlayerValue(e.target.value);
  };

  const handlePlayerKeyDown = (e) => {
    if (e.key === "Enter") {
      // Manually trigger blur to remove focus
      if (playerInputRef.current) {
        playerInputRef.current.blur(); // Remove focus from the input field
      }
    }
  };

  const storePlayerChange = (e) => {
    // Retrieve the existing latestEntriesFilters object from localStorage
    const latestEntriesFilters =
      JSON.parse(localStorage.getItem("latestEntriesFilters")) || {};

    // Update the player value in the object
    const updatedFilters = { ...latestEntriesFilters, player: e.target.value };

    // Store the updated object back in localStorage
    localStorage.setItem(
      "latestEntriesFilters",
      JSON.stringify(updatedFilters)
    );

    setPlayerValueUpdated(e.target.value);
  };

  const handleEntriesChange = (e) => {
    setEntriesValue(e.target.value);

    // Retrieve the existing latestEntriesFilters object from localStorage
    const latestEntriesFilters =
      JSON.parse(localStorage.getItem("latestEntriesFilters")) || {};

    // Update the player value in the object
    const updatedFilters = { ...latestEntriesFilters, entries: e.target.value };

    // Store the updated object back in localStorage
    localStorage.setItem(
      "latestEntriesFilters",
      JSON.stringify(updatedFilters)
    );

    setEntriesValueUpdated(e.target.value);
  };

  // On filter update
  useEffect(() => {
    // Get value from localStorage when component mounts
    const latestEntriesFilters = localStorage.getItem("latestEntriesFilters");
    let query = "";
    if (latestEntriesFilters) {
      const parsedLatestEntriesFilter = JSON.parse(latestEntriesFilters);
      setPlayerValue(parsedLatestEntriesFilter.player);
      setEntriesValue(parsedLatestEntriesFilter.entries);
      query = buildQuery(
        parsedLatestEntriesFilter.player,
        parsedLatestEntriesFilter.entries
      );
    }
    const API_HOST = process.env.REACT_APP_API;
    // fetch data
    const dataFetch = async () => {
      const data = await (
        await fetch(`${API_HOST}public/get-last-10${query}`)
      ).json();
      if (data) {
        setTableData(getData(data, isMobile));
        setLoading(false);
      }
    };

    dataFetch();
  }, [isMobile, playerValueUpdated, entriesValueUpdated]);

  return (
    <>
      {/* Header */}
      <SectionHeader
        title="LATEST PKS"
        action={
          <>
            <Form.Control
              ref={playerInputRef}
              type="text"
              placeholder="Filter by Player"
              aria-label="Filter by player"
              onChange={handlePlayerChange}
              onBlur={storePlayerChange}
              onKeyDown={handlePlayerKeyDown}
              defaultValue={playerValue}
              autoComplete="false"
              style={{ width: "12rem" }}
            />
            <Form.Label htmlFor="last10-entries" className="mb-0 text-muted small">
              Rows
            </Form.Label>
            <Form.Control
              id="last10-entries"
              as="select"
              value={entriesValue}
              onChange={handleEntriesChange}
              style={{ width: "5rem" }}
            >
              <option value="" disabled>
                Number of entries
              </option>
              <option value="10">10</option>
              <option value="20">20</option>
            </Form.Control>
          </>
        }
      />

      <div style={styles.wrapper}>
        {(loading && (
          <div className="spinner-border text-warning" role="status">
            <span className="sr-only" />
          </div>
        )) || (
          <Table2
            headers={isMobile ? mobileColumns : desktopColumns}
            desktopHeaderSizes={desktopColumnSizes}
            data={tableData || []}
          />
        )}
      </div>
    </>
  );
}

export default Last10Feed;
